import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { alpha, Box, type SxProps, type Theme } from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";

import { Icon, type IconType } from "../Icon";
import { Text } from "../Text";

export const INLINE_ALERT_VARIANTS = {
  danger: "danger",
  warning: "warning",
  neutral: "neutral",
} as const;

export type InlineAlertVariant = (typeof INLINE_ALERT_VARIANTS)[keyof typeof INLINE_ALERT_VARIANTS];

export interface InlineAlertProps {
  /**
   * The message to display in the alert. Ignored when `children` is provided.
   */
  message?: string;
  /**
   * Rich content; takes precedence over `message`.
   */
  children?: React.ReactNode;
  /**
   * Color treatment. Defaults to `danger` to preserve back-compat with the
   * existing string-message callers.
   */
  variant?: InlineAlertVariant;
  /**
   * Berlin icon override. When omitted, danger falls back to the MUI
   * ErrorOutline icon (preserves the legacy look); warning/neutral default
   * to `warning` and `clock-outlined` respectively.
   */
  iconType?: IconType;
  /**
   * Additional styles for the alert container
   */
  sx?: SxProps<Theme>;
}

const DANGER_BACKGROUND_OPACITY = 0.06;
const DANGER_BORDER_OPACITY = 0.2;
const WARNING_BACKGROUND_RGBA = "rgba(206, 114, 16, 0.1)";

const DEFAULT_ICON_BY_VARIANT: Record<Exclude<InlineAlertVariant, "danger">, IconType> = {
  warning: "warning",
  neutral: "clock-outlined",
};

export function InlineAlert(props: InlineAlertProps) {
  const { message, children, variant = "danger", iconType, sx } = props;
  const isDanger = variant === "danger";
  const content = children ?? message;

  return (
    <Box
      role={variant === "warning" || isDanger ? "alert" : undefined}
      aria-live={variant === "warning" || isDanger ? "polite" : undefined}
      sx={mergeSxProps(
        (theme: Theme) => ({
          display: "flex",
          alignItems: "center",
          gap: theme.spacing(3),
          padding: theme.spacing(4),
          borderRadius: theme.borderRadius?.xLarge,
          backgroundColor: isDanger
            ? alpha(theme.palette.base!.danger, DANGER_BACKGROUND_OPACITY)
            : variant === "warning"
              ? WARNING_BACKGROUND_RGBA
              : theme.palette.background?.muted,
          border: isDanger
            ? `1px solid ${alpha(theme.palette.base!.danger, DANGER_BORDER_OPACITY)}`
            : undefined,
        }),
        sx
      )}
    >
      {isDanger && iconType === undefined ? (
        <ErrorOutlineIcon
          sx={(theme: Theme) => ({
            color: theme.palette.base?.danger,
            fontSize: 18,
            flexShrink: 0,
          })}
        />
      ) : (
        <Icon
          type={
            iconType ?? DEFAULT_ICON_BY_VARIANT[variant as Exclude<InlineAlertVariant, "danger">]
          }
          size="small"
          sx={(theme: Theme) => ({
            color:
              variant === "warning"
                ? theme.palette.warning?.main
                : isDanger
                  ? theme.palette.base?.danger
                  : theme.palette.text?.secondary,
            flexShrink: 0,
          })}
        />
      )}
      {typeof content === "string" ? (
        <Text
          bold={isDanger}
          variant="body2"
          sx={(theme: Theme) => ({
            color: isDanger ? theme.palette.base?.danger : theme.palette.text?.primary,
          })}
        >
          {content}
        </Text>
      ) : (
        <Text variant="body2" sx={(theme: Theme) => ({ color: theme.palette.text?.primary })}>
          {content}
        </Text>
      )}
    </Box>
  );
}
